import {
  useShareAddMemberWithPermissionsMutation,
  ShareAddMemberWithPermissionsMutationVariables,
  ShareAddMemberWithPermissionsMutation,
  GetShareMembershipsDocument,
  GetShareMembershipsQueryVariables,
} from 'gqlTypes'
import { useCallback, useState } from 'react'
import { ErrorHandler } from 'modules/tracing'
import { useToast } from 'containers/ToastManager'
import { useModalContext } from 'containers/ModalManager/ModalContext'

type Permissions = ShareAddMemberWithPermissionsMutationVariables['permissions']

const includesConfirmationRequiredError = (
  data: ShareAddMemberWithPermissionsMutation | null | undefined
) =>
  data?.shareAddMember?.errors?.some(e => e?.code === 'CONFIRMATION_REQUIRED')

const enableCommentsIfEditPermissions = (permissions: Permissions) =>
  permissions.privateAccessLevel === 'EDIT' || permissions.commentsEnabled

export const useAddMemberByEmail = () => {
  const { showToast } = useToast()
  const { hideModal } = useModalContext()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [variables, setVariables] = useState<
    ShareAddMemberWithPermissionsMutationVariables | undefined
  >(undefined)

  const reset = useCallback(() => {
    setDialogOpen(false)
    setVariables(undefined)
  }, [])

  const [
    addMemberWithPermissions,
    { loading: addMemberByEmailLoading },
  ] = useShareAddMemberWithPermissionsMutation({
    redirectErrors: false,
    onCompleted(data) {
      if (includesConfirmationRequiredError(data)) {
        setDialogOpen(true)
        return
      }

      reset()

      if (!data?.shareAddMember) {
        ErrorHandler.shouldNeverHappen(
          'Share add member permissions mutation should return valid data'
        )
        return
      }

      const { shareAddMember } = data

      if (shareAddMember.errors.length > 0) {
        showToast(shareAddMember?.errors[0]!.message, 'negative')
        return
      }

      if (shareAddMember.share?.userCanUpdateSettings === false) {
        hideModal()
        showToast("You can no longer view this document's settings")
        return
      }

      if (shareAddMember.successful) {
        showToast(`Invitation sent to ${shareAddMember.membership?.email}`)
      }
    },
    onError() {
      reset()
      showToast(
        'There was a problem inviting the member. Please try again.',
        'negative'
      )
    },
    awaitRefetchQueries: true,
    refetchQueries: ({ data }) => {
      const typedData = data as ShareAddMemberWithPermissionsMutation
      if (
        includesConfirmationRequiredError(typedData) ||
        !typedData.shareAddMember?.successful
      ) {
        return []
      }

      const variables: GetShareMembershipsQueryVariables = {
        shareIdentifier: typedData.shareAddMember.share!.identifier,
      }

      return [{ query: GetShareMembershipsDocument, variables }]
    },
  })

  const addMemberByEmail = useCallback(
    (
      variables: ShareAddMemberWithPermissionsMutationVariables,
      confirmed: boolean = false
    ) => {
      setVariables(variables)
      return addMemberWithPermissions({
        variables: {
          ...variables,
          permissions: {
            ...variables.permissions,
            commentsEnabled: enableCommentsIfEditPermissions(
              variables.permissions
            ),
            confirmed,
          },
        },
      })
    },
    [addMemberWithPermissions]
  )

  const getAddMemberByEmailConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        reset()
      },
      onConfirm() {
        if (variables) addMemberByEmail(variables, true)
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading: addMemberByEmailLoading,
      } as const,
    }),
    [addMemberByEmail, addMemberByEmailLoading, variables, reset]
  )

  return {
    addMemberByEmail,
    addMemberByEmailLoading: addMemberByEmailLoading,
    getAddMemberByEmailConfirmDialogProps,
    addMemberByEmailConfirmDialogOpen: dialogOpen,
  }
}
